import { ReactNode } from "react";
import { Center, Flex, Spinner, Text, Icon } from "@chakra-ui/react";
import { CircleAlert } from "lucide-react";
import { IEmployee, useEmployeeSelf } from "../services/useEmployee";
import useIsMobile from "@ui/hooks/useIsMobile";
import UserProfile from "@/components/UserProfile";
import { DateTime } from "luxon";
import Page from "@ui/components/Page";
import { useACL } from "@ui/components/document/DocumentACL";

interface IProps {
  children: ReactNode;
}

export function TerminatedUser({ children }: IProps) {
  const isMobile = useIsMobile();
  const { isPermitted } = useACL();
  const isSuperAdmin = isPermitted(["group:super-admin"], undefined, undefined);
  const isEmployeeAdmin = isPermitted(
    ["group:employee-admin"],
    undefined,
    undefined,
  );
  const isAdmin = isPermitted(["group:admin"], undefined, undefined);
  const isGuestBookingAdmin = isPermitted(
    ["group:guest-booking-admin"],
    undefined,
    undefined,
  );

  const { data: employee, isLoading } = useEmployeeSelf<IEmployee>();
  const today = DateTime.now().toISO();
  const lastWorkingDate = employee?.lastWorkingDate
    ? typeof employee.lastWorkingDate === "string"
      ? DateTime.fromISO(employee.lastWorkingDate).toISO()
      : employee.lastWorkingDate.toISO?.()
    : undefined;
  const isTerminated =
    employee?.status?.label?.toLowerCase() === "terminated" ||
    (!!lastWorkingDate && lastWorkingDate <= today);

  if (isLoading || !employee) {
    return <Spinner size="lg" />;
  }

  if (isTerminated) {
    return (
      <Page
        title={
          !isMobile &&
          (isSuperAdmin ||
            isEmployeeAdmin ||
            isAdmin ||
            isGuestBookingAdmin) ? null : (
            <UserProfile />
          )
        }
      >
        <AccessForbidden
          title={"Your account has been deactivated."}
          icon={CircleAlert}
          description="Please contact the administrator to restore access."
        />
      </Page>
    );
  }

  return <>{children}</>;
}

interface IAccessForbidden {
  title: string;
  description?: string;
  icon?: React.ElementType;
}

export function AccessForbidden({
  title,
  description,
  icon,
}: IAccessForbidden) {
  return (
    <Center minH="100vh">
      <Flex direction="column" align={"center"}>
        <Icon as={icon} w={12} h={12} mb={4} color="red.600" />
        <Text fontSize="lg" fontWeight="semibold" color="red.600">
          {title}
        </Text>
        <Text color="gray.600" mt={2} textAlign="center">
          {description}
        </Text>
      </Flex>
    </Center>
  );
}
